import { useState, useMemo } from 'react';
import { Link } from '@/lib/react-router-dom-shim';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ChevronDown, ChevronUp, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PopularHotel {
  slug: string;
  name: string;
  name_slug: string;
  city: string | null;
  visit_count: number;
}

interface FooterPopularHotelsProps {
  initialLimit?: number;
  expandedLimit?: number;
}

// Helper function to clean hotel name - remove city suffix
const cleanHotelName = (name: string, city: string | null): string => {
  if (!city) return name;

  const patterns = [
    new RegExp(`\\s*[-–|]\\s*${city}\\s*$`, 'i'),
    new RegExp(`\\s*,\\s*${city}\\s*$`, 'i'),
    new RegExp(`\\s+${city}\\s*$`, 'i'),
  ];

  let cleanName = name;
  for (const pattern of patterns) {
    cleanName = cleanName.replace(pattern, '');
  }

  return cleanName.trim() || name;
};

export const FooterPopularHotels = ({
  initialLimit = 9,
  expandedLimit = 27,
}: FooterPopularHotelsProps) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: hotels = [], isLoading } = useQuery({
    queryKey: ['footer-popular-hotels', expandedLimit],
    queryFn: async (): Promise<PopularHotel[]> => {
      const { data, error } = await supabase
        .from('hotel_static_pages')
        .select('slug, name, name_slug, city, visit_count')
        .order('visit_count', { ascending: false })
        .limit(expandedLimit);
      if (error) throw error;
      return (data ?? []) as PopularHotel[];
    },
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });

  const displayedHotels = useMemo(() => {
    const hotelsToShow = isExpanded ? hotels : hotels.slice(0, initialLimit);
    return hotelsToShow.map((hotel) => ({
      ...hotel,
      displayName: cleanHotelName(hotel.name, hotel.city),
    }));
  }, [hotels, isExpanded, initialLimit]);

  if (isLoading || hotels.length === 0) {
    return null;
  }

  const hasMore = hotels.length > initialLimit;

  return (
    <div className="pt-6 border-t border-primary-foreground/20">
      <h4 className="mb-4 font-semibold text-sm text-primary-foreground flex items-center gap-2">
        <Building2 className="h-4 w-4" />
        {t('footer.popularHotels') || 'Hoteles Populares'}
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5">
        {displayedHotels.map((hotel) => (
          <Link
            key={hotel.slug}
            to={`/hotel/${hotel.slug}`}
            className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors truncate"
            title={hotel.name}
          >
            {hotel.displayName}
          </Link>
        ))}
      </div>
      {hasMore && (
        <Button
          variant="link"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-3 p-0 h-auto text-xs text-primary-foreground/60 hover:text-primary-foreground"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="h-3 w-3 mr-1" />
              {t('common.showLess') || 'Ver menos'}
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3 mr-1" />
              {t('footer.seeMoreHotels') || `Ver más (${hotels.length - initialLimit})`}
            </>
          )}
        </Button>
      )}
    </div>
  );
};
